import {
  DEFAULT_BXGY,
  discountKindOf,
  type ShopifyBxgyTerms,
  type ShopifyDiscountKind,
  type ShopifyDiscountKindTerms,
  type ShopifyDiscountValueType,
  type ShopifyFreeShippingTerms,
} from '@discovr/features/shopify';
import { MAX_MINIMUM_REQUIREMENT_VALUE } from './shopifyCodeUtils';
import { discountValueSummary } from './shopifyCouponSummary';
import { formatStoreMoney } from './shopifyCurrency';

/** Shopify admin's names for the code-discount types Discovr mints. */
export const DISCOUNT_KIND_LABEL: Record<ShopifyDiscountKind, string> = {
  basic: 'Amount off',
  free_shipping: 'Free shipping',
  bxgy: 'Buy X get Y',
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

let regionNames: Intl.DisplayNames | undefined;
let knownRegions: Intl.DisplayNames | undefined;

/** A two-letter code the runtime knows as a region; Shopify rejects others. */
export function isKnownCountry(code: string): boolean {
  try {
    knownRegions ??= new Intl.DisplayNames(['en'], { type: 'region', fallback: 'none' });
    return knownRegions.of(code) !== undefined;
  } catch {
    return true;
  }
}

/** "India" for "IN"; the code itself when the runtime can't name it. */
export function countryName(code: string): string {
  try {
    regionNames ??= new Intl.DisplayNames(['en'], { type: 'region' });
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
}

export function countriesSummary(fs: ShopifyFreeShippingTerms | undefined): string {
  const countries = fs?.countries ?? [];
  if (countries.length === 0) return 'All countries';
  if (countries.length === 1) return countryName(countries[0] ?? '');
  return `${countries.length} countries`;
}

/** "Free shipping" or "Free shipping up to ₹100". */
export function freeShippingLabel(
  fs: ShopifyFreeShippingTerms | undefined,
  currencyCode: string,
): string {
  return fs?.max_shipping_price
    ? `Free shipping up to ${formatStoreMoney(fs.max_shipping_price, currencyCode)}`
    : 'Free shipping';
}

/** "Buy 2" or "Spend ₹500". */
export function bxgyBuysLabel(b: ShopifyBxgyTerms, currencyCode: string): string {
  return b.buys_type === 'amount'
    ? `Spend ${formatStoreMoney(b.buys_amount ?? 0, currencyCode)}`
    : `Buy ${b.buys_quantity ?? 0}`;
}

/** "get 1 free" or "get 2 at 50% off". */
export function bxgyGetsLabel(b: ShopifyBxgyTerms): string {
  const value = b.gets_pct >= 100 ? 'free' : `at ${b.gets_pct}% off`;
  return `get ${b.gets_quantity} ${value}`;
}

export function bxgyLabel(b: ShopifyBxgyTerms | undefined, currencyCode: string): string {
  if (!b) return DISCOUNT_KIND_LABEL.bxgy;
  return `${bxgyBuysLabel(b, currencyCode)}, ${bxgyGetsLabel(b)}`;
}

/** "1 product" / "2 collections" — what a BXGY customer gets. */
export function bxgyGetsItemsSummary(b: ShopifyBxgyTerms | undefined): string {
  if (!b) return '—';
  if (b.gets_target_type === 'collection') {
    const n = b.gets_collections?.length ?? 0;
    return n === 0 ? 'No collections selected' : plural(n, 'collection');
  }
  const n = b.gets_products?.length ?? 0;
  return n === 0 ? 'No products selected' : plural(n, 'product');
}

/** A coupon's headline: the amount off for basic, else the type's own terms. */
export function couponValueLabel(
  terms: ShopifyDiscountKindTerms & {
    value_type?: string;
    discount_amount?: number;
    applies_on_each_item?: boolean;
  },
  headlineValue: number,
  currencyCode: string,
): string {
  switch (discountKindOf(terms)) {
    case 'free_shipping':
      return freeShippingLabel(terms.free_shipping, currencyCode);
    case 'bxgy':
      return bxgyLabel(terms.bxgy, currencyCode);
    default:
      return discountValueSummary(
        (terms.value_type ?? '') as ShopifyDiscountValueType,
        headlineValue,
        terms.discount_amount ?? 0,
        currencyCode,
        terms.applies_on_each_item,
      );
  }
}

/** Parses "in, us gb" into unique two-letter country codes. */
export function parseCountryCodes(raw: string): string[] {
  const codes = raw
    .split(/[\s,;]+/)
    .map((c) => c.trim().toUpperCase())
    .filter((c) => /^[A-Z]{2}$/.test(c) && isKnownCountry(c));
  return [...new Set(codes)];
}

/** Switching kind keeps each kind's settings in the form so switching back
 *  restores them; only the active kind is ever sent (kindPayload). */
export function withKind(
  t: ShopifyDiscountKindTerms,
  kind: ShopifyDiscountKind,
): ShopifyDiscountKindTerms {
  return {
    ...t,
    discount_kind: kind,
    free_shipping: kind === 'free_shipping' ? (t.free_shipping ?? {}) : t.free_shipping,
    bxgy: kind === 'bxgy' ? (t.bxgy ?? DEFAULT_BXGY) : t.bxgy,
  };
}

/** Shopify's limits Discovr mirrors: items per BXGY side, and the buy, get and per-order
 *  quantities (inclusive, as the backend's maxBxgyQuantity). */
export const MAX_SHIPPING_PRICE = 1_000_000;
export const MAX_BXGY_TARGETS = 50;
export const MAX_BXGY_QUANTITY = 10_000;

interface KindErrorFields extends ShopifyDiscountKindTerms {
  products?: unknown[];
  collections?: unknown[];
  variants?: unknown[];
}

/** The first problem with a kind's own settings, for the submit toast. */
export function discountKindError(t: KindErrorFields): string | undefined {
  const kind = discountKindOf(t);
  if (kind === 'free_shipping') {
    const max = t.free_shipping?.max_shipping_price;
    if (max !== undefined && (!Number.isFinite(max) || max <= 0 || max > MAX_SHIPPING_PRICE))
      return `Maximum shipping rate must be greater than 0 and at most ${MAX_SHIPPING_PRICE.toLocaleString()}.`;
    return undefined;
  }
  if (kind !== 'bxgy') return undefined;
  const b = t.bxgy ?? DEFAULT_BXGY;
  if (
    b.buys_type === 'amount'
      ? !(
          b.buys_amount &&
          Number.isFinite(b.buys_amount) &&
          b.buys_amount > 0 &&
          b.buys_amount <= MAX_MINIMUM_REQUIREMENT_VALUE
        )
      : !(b.buys_quantity && b.buys_quantity >= 1 && b.buys_quantity <= MAX_BXGY_QUANTITY)
  ) {
    return b.buys_type === 'amount'
      ? `Customer buys amount must be greater than 0 and at most ${MAX_MINIMUM_REQUIREMENT_VALUE.toLocaleString()}.`
      : `Customer buys quantity must be between 1 and ${MAX_BXGY_QUANTITY.toLocaleString()}.`;
  }
  if (!(b.gets_quantity >= 1 && b.gets_quantity <= MAX_BXGY_QUANTITY))
    return `Customer gets quantity must be between 1 and ${MAX_BXGY_QUANTITY.toLocaleString()}.`;
  if (!(b.gets_pct > 0 && b.gets_pct <= 100))
    return 'Customer gets discount must be between 1 and 100%.';
  const gets = b.gets_target_type === 'collection' ? b.gets_collections : b.gets_products;
  if (!gets || gets.length === 0) return 'Choose the items the customer gets.';
  if (gets.length > MAX_BXGY_TARGETS) {
    return `Customer gets can have at most ${MAX_BXGY_TARGETS} products or collections.`;
  }
  if (
    (t.products?.length ?? 0) + (t.variants?.length ?? 0) > MAX_BXGY_TARGETS ||
    (t.collections?.length ?? 0) > MAX_BXGY_TARGETS
  ) {
    return `Customer buys can have at most ${MAX_BXGY_TARGETS} products, variants or collections.`;
  }
  if (
    b.uses_per_order_limit !== undefined &&
    (b.uses_per_order_limit < 1 || b.uses_per_order_limit > MAX_BXGY_QUANTITY)
  ) {
    return `Maximum uses per order must be between 1 and ${MAX_BXGY_QUANTITY.toLocaleString()}.`;
  }
  return undefined;
}

/** The coupon form's main card title, after Shopify admin's own. */
export function kindCardTitle(kind: ShopifyDiscountKind): string {
  if (kind === 'free_shipping') return 'Free shipping';
  if (kind === 'bxgy') return 'Buy X get Y';
  return 'Amount off products';
}
